import { Popover } from "@radix-ui/themes";
import { observer } from "mobx-react-lite";
import type React from "react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useOnClickOutside } from "@hooks/useOnClickOutside";
import { Sheet } from "@src/ui-kit/Sheet";

import type { TFunction } from "i18next";
import { MarketListContent } from "./MarketListContent";
import { MarketSelectTrigger } from "./MarketSelectTrigger";
import { PopoverContent, SheetContent } from "./styles";

interface MarketSelectProps {
  useMobileSheet?: boolean;
}

export const MarketSelect: React.FC<MarketSelectProps> = ({ useMobileSheet = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null!);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
  };

  if (useMobileSheet) {
    return (
      <>
        <MarketSelectTrigger ref={triggerRef} onClick={() => setOpen(true)} />
        <Sheet data-testid="market-select:sheet" open={open} showDragHandle onOpenChange={handleOpenChange}>
          <SheetContent>
            <MarketListContent t={t} navigate={navigate} onClose={() => setOpen(false)} />
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <Popover.Root modal={true} open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger>
        <MarketSelectTrigger ref={triggerRef} />
      </Popover.Trigger>
      <MarketSelectPopoverContent t={t} navigate={navigate} triggerRef={triggerRef} onClose={() => setOpen(false)} />
    </Popover.Root>
  );
};

// Popover content - observer only when open
const MarketSelectPopoverContent = observer(
  ({
    t,
    navigate,
    triggerRef,
    onClose,
  }: {
    t: TFunction<"translation", undefined>;
    navigate: (path: string) => void;
    triggerRef: React.RefObject<HTMLButtonElement>;
    onClose: () => void;
  }) => {
    const marketRef = useRef<HTMLDivElement>(null!);

    useOnClickOutside([marketRef, triggerRef], onClose);

    return (
      <PopoverContent data-testid="market-select:popover" ref={marketRef} alignOffset={-8} sideOffset={12}>
        <MarketListContent t={t} navigate={navigate} onClose={onClose} />
      </PopoverContent>
    );
  },
);
